import { ExpandMore } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { type StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import { Details } from '@repo/ui/Details';
import {
  accordionLabelToDomIdSegment,
  sanitizeOptionalInstanceDomIdSegment,
} from '../../../utils/sanitizeDomIdSegment';
import { StructuredContentRenderer } from '../..';

export type AccordionItemProps = StructuredContentDocument<{
  label: string;
  labelCollapsed: string;
  initialStateExpanded: boolean;
  supportsSpacing?: boolean;
  children: StructuredContentDocument;
  instanceId?: string;
}>;

type AccordionItemRenderProps = AccordionItemProps & {
  structuredContentChildIndex?: number;
};

export const AccordionItem = ({
  props,
  structuredContentChildIndex = 0,
}: AccordionItemRenderProps) => {
  if (!props || !props.label) return null;

  const fromInstance = props.instanceId?.trim()
    ? sanitizeOptionalInstanceDomIdSegment(props.instanceId)
    : '';

  const panelContentId = fromInstance
    ? `accordion-content-${fromInstance}`
    : `accordion-content-${accordionLabelToDomIdSegment(props.label, 'accordion-item')}-${structuredContentChildIndex}`;

  return (
    <Box
      component="li"
      sx={{
        '&:last-of-type details': {
          borderBottom: '1px solid',
          borderColor: 'neutral.lighter',
        },
      }}
    >
      <Details
        variant="simple"
        detailsProps={{
          open: props.initialStateExpanded,
          sx: {
            borderTop: '1px solid',
            borderColor: 'neutral.lighter',
            backgroundColor: 'inherit',
          },
        }}
        summaryComponentProps={{
          'aria-controls': panelContentId,
          sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingX: 2,
            paddingY: '12px',
            '&:hover': {
              outline: { xs: 'none', sm: '2px solid #54A0E8' },
              bgcolor: 'blues.lightest',
            },
          },
        }}
        SummaryComponent={
          <>
            <Typography component="span" variant="textBold">
              {props.label}
            </Typography>
            <ExpandMore className="expand-icon" sx={{ fontSize: 20, opacity: 0.54 }} />
          </>
        }
      >
        <Box id={panelContentId} sx={{ padding: 3 }}>
          <StructuredContentRenderer doc={props.children} />
        </Box>
      </Details>
    </Box>
  );
};
